<template>
  <div
    v-if="isBounced"
    class="flex items-center p-2 cursor-pointer"
    :class="width"
  >
    <div class="status_indicator bounced ml-3" @click="click">
      <ThemeImage :icon="imageNames.WHITE_DOT" alt="white_dot" class="size-2" />
      <span>Bounced</span>
    </div>
  </div>

  <div
    v-else
    class="flex items-center cursor-pointer gap-2"
    :class="width"
    @click="click"
  >
    <EngagementTooltip
      :show-tooltip="showDeliveredTooltip"
      :is-active="isDelivered"
      :timestamps="
        timestampData.deliveredTimestamp
          ? [timestampData.deliveredTimestamp]
          : []
      "
      :title="'Sent at'"
      :type="engagementTooltipTypes.DELIVERED"
      :count="0"
      :show-count="false"
      source="engagement"
    />

    <EngagementTooltip
      :show-tooltip="showOpenedTooltip"
      :is-active="isOpenedActive"
      :timestamps="timestampData.openedTimestamps || []"
      :title="'Opened at'"
      :type="engagementTooltipTypes.OPENED"
      :count="openedCount"
      :show-count="openedCount > 0"
      :original-count="timestampData.originalOpenedCount"
      source="engagement"
    />

    <EngagementTooltip
      :show-tooltip="showClickedTooltip"
      :is-active="isClickedActive"
      :timestamps="timestampData.clickedTimestamps || []"
      :title="'Clicked at'"
      :type="engagementTooltipTypes.CLICKED"
      :count="clickedCount"
      :show-count="clickedCount > 0"
      :original-count="timestampData.originalClickedCount"
      source="engagement"
    />

    <EngagementTooltip
      :show-tooltip="showRepliedTooltip"
      :is-active="isRepliedActive"
      :timestamps="timestampData.repliedTimestamps || []"
      :title="'Replied at'"
      :type="engagementTooltipTypes.REPLIED"
      :count="repliedCount"
      :show-count="repliedCount > 0"
      :original-count="timestampData.originalRepliedCount"
      source="engagement"
    />
  </div>
</template>

<script setup>
import { defineProps } from "vue";
import eventBus from "@/src/utils/eventBus.js";
import {
  emailStages,
  engagementTooltipTypes,
} from "@/src/constants/emailConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import EngagementTooltip from "./EngagementTooltip.vue";
import { computed } from "vue";

const props = defineProps({
  value: {
    type: Object,
    required: true,
  },
  row: {
    type: Object,
    default: () => ({}),
  },
  width: {
    type: String,
    default: "w-full",
  },
  showHoverDetails: {
    type: Boolean,
    default: false,
  },
  timestampData: {
    type: Object,
    default: () => ({
      deliveredTimestamp: null,
      openedTimestamps: [],
      clickedTimestamps: [],
      repliedTimestamps: [],
      originalOpenedCount: null,
      originalClickedCount: null,
      originalRepliedCount: null,
    }),
  },
});

const engagement = props.value;
const isReplied = engagement.status === emailStages.REPLIED;
const isClicked = engagement.status === emailStages.CLICKED || isReplied;
const isOpened = engagement.status === emailStages.OPENED || isClicked;
const isDelivered =
  engagement.status === emailStages.DELIVERED ||
  isOpened ||
  isClicked ||
  isReplied;

const openedCount = engagement.opened_count || 0;
const clickedCount = engagement.clicked_count || 0;
const repliedCount = engagement.replied_count || 0;
const isBounced = engagement.status === emailStages.BOUNCED;

const isOpenedActive = openedCount > 0;
const isClickedActive = clickedCount > 0;
const isRepliedActive = repliedCount > 0;

const showDeliveredTooltip = computed(
  () => props.showHoverDetails && props.timestampData.deliveredTimestamp
);
const showOpenedTooltip = computed(
  () =>
    props.showHoverDetails &&
    props.timestampData.openedTimestamps &&
    props.timestampData.openedTimestamps.length > 0
);
const showClickedTooltip = computed(
  () =>
    props.showHoverDetails &&
    props.timestampData.clickedTimestamps &&
    props.timestampData.clickedTimestamps.length > 0
);
const showRepliedTooltip = computed(
  () =>
    props.showHoverDetails &&
    props.timestampData.repliedTimestamps &&
    props.timestampData.repliedTimestamps.length > 0
);

const click = () => {
  eventBus.emit("emails/showActivity", props.row);
};
</script>
<style scoped>
.status_indicator {
  display: flex;
  padding: 3px 8px 3px 4px;
  align-items: center;
  gap: 4px;
}

.bounced {
  background: #ac1c1e;
  color: white;
  cursor: default;
  width: fit-content;
  border-radius: 24px;
}

.size-2 {
  width: 15px;
  height: 15px;
}
.bounced span {
  color: #fff;
  font-family: "Proxima Nova";
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}
</style>
